<template>
	<card title="Charts" tag="section" v-if="hasReportData">
		<div class="is-flex is-justify-content-space-between">
			<div>
				<o-switch v-model="showCompare">Show Comparison</o-switch>
			</div>
			<div class="is-flex is-justify-content-space-around">
				<o-field label="Year" class="ml-5" v-if="metric === 'forecast'">
					<o-select v-model="year">
						<option v-for="y in years" :key="y" :value="y">{{ y }}</option>
					</o-select>
				</o-field>
				<o-field label="Show" class="ml-5">
					<o-select v-model="metric">
						<option value="totalBillings">Total Billings</option>
						<option value="teamingValue">Teaming Value</option>
						<option value="teamingRevenue">Teaming Revenue</option>
						<option value="executiveSearch">Executive Search</option>
						<option value="consulting">Leadership Consulting</option>
						<option value="im">Interim Management</option>
						<option value="filledJobValue">Average Filled Job Value</option>

						<option value="taken">Taken</option>
						<option value="filled">Filled</option>
						<!--
						<option value="headcount">Headcount</option>
						<option value="forecast">Forecast</option>
						-->
					</o-select>
				</o-field>
			</div>
		</div>
		<chart-legend show-current show-compare show-dashed></chart-legend>
		<line-chart :datasets="datasets" :labels="labels" :options="options"></line-chart>
	</card>
</template>
<script>
	import { ref, computed, inject, watch, onMounted, onBeforeUnmount } from "vue";
	import { lineChart, lineChartHelper, legend } from "@/components/charts";
	import { stringUtil } from "@/services/utils";
	import { compare_datasets } from "./compareOutput.js";

	export default {
		components: {
			lineChart,
			chartLegend: legend
		},
		props: {
			report: {
				type: Object,
				default: () => {
					return {
						results: [],
						compare: []
					};
				}
			},
			breakdown: {
				type: Object,
				default: () => {
					return undefined;
				}
			},
			colors: {
				type: Array,
				default: () => {
					return ["#00aeef", "#d9017a"];
				}
			},
			sampleSize: {
				type: Number,
				default: null
			}
		},
		setup(props, context) {
			const http = inject("$http");
			const $financial = inject("$financial");
			const moment = inject("$moment");

			const showCompare = ref(true);
			const metric = ref("totalBillings");
			const year = ref(moment.utc().year());
			const years = ref([
				moment.utc().year(),
				moment.utc().subtract(1, "year").year(),
				moment.utc().subtract(2, "year").year()
			]);

			const datasets = ref([]);

			const options = ref({
				responsive: true,
				plugins: {
					legend: {
						display: false
					},
					tooltip: {
						callbacks: {
							title: (tooltipItems) => {
								const tooltip = tooltipItems[0];
								const labelType = tooltip.dataset.label;
								if (labelType == "Previous Period") {
									const label = moment(tooltip.label, "MMM YYYY");
									label.add(-1, "year");
									return label.format("MMM YYYY");
								} else return tooltip.label;
							},
							label: (tooltipItem) => {
								return new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 }).format(
									tooltipItem.raw
								);
							}
						}
					}
				},
				scales: {
					y: {
						suggestedMin: 0
					},
					x: {
						ticks: {
							maxTicksLimit: props.sampleSize,
							sampleSize: props.sampleSize
						}
					}
				}
			});

			const results = computed(() => {
				if (props.breakdown) {
					return props.breakdown.api.BreakdownFilterResults(props.report.results);
				}
				return props.report.results;
			});

			const compare = computed(() => {
				if (props.breakdown) {
					return props.breakdown.api.BreakdownFilterResults(props.report.compare);
				}
				return props.report.compare;
			});

			const remainder = 0;
			const factor = 1;

			const dateField = computed(() => {
				switch (metric.value) {
					case "totalBillings":
					case "teamingValue":
					case "teamingRevenue":
					case "executive":
					case "executiveSearch":
					case "consulting":
					case "im":
					case "strategyBillings":
					case "strategy":
					case "headcount":
						return "invoiceMonth";
					case "filledJobValue":
					case "filled":
					case "timeToFill":
						return "statusMonth";
					case "strategyTaken":
					case "taken":
						return "takenMonth";
					default:
						return "createdMonth";
				}
			});

			const cache = {};
			const cacheKey = computed(() => {
				return stringUtil.toHashCode(
					metric.value +
						moment.utc(props.report.filter.date.startDate).format("YYYY-MM-DD") +
						moment.utc(props.report.filter.date.endDate).format("YYYY-MM-DD") +
						JSON.parse(JSON.stringify(props.report.results))
				);
			});
			const SetCache = (item) => {
				if (!cache[cacheKey.value]) {
					cache[cacheKey.value] = item;
				}
			};

			const hasReportData = computed(() => {
				return (
					props.report.results &&
					props.report.results.length > 0 &&
					props.report.compare &&
					props.report.compare.length > 0
				);
			});

			const ToQuarter = (d) => {
				return "Q" + moment(d).format("Q YY");
			};

			const isLastQuarter = computed(() => {
				const currentQuarter = ToQuarter(moment.utc().toDate());
				const lastLabel = ToQuarter(
					moment.utc("2" + labels.value[labels.value.length - 1], "D MMM YYYY").toDate()
				);
				if (lastLabel === currentQuarter) {
					return true;
				} else {
					return false;
				}
			});

			const labels = computed(() => {
				if (!props.report.results) return [];

				const labels = [];
				const endDate = moment(props.report.filter.date.endDate);

				while (endDate > moment(props.report.filter.date.startDate)) {
					labels.push(moment(endDate).format("MMM YYYY"));

					endDate.add(-1, "month");
				}

				return labels.reverse();
			});

			const compareLabels = computed(() => {
				if (!props.report.results) return [];

				const labels = [];
				const endDate = moment(props.report.filter.date.endDate).add(-1, "year");
				const startDate = moment(props.report.filter.date.startDate).add(-1, "year");

				while (endDate > startDate) {
					labels.push(moment(endDate).format("MMM YYYY"));

					endDate.add(-1, "month");
				}

				return labels.reverse();
			});

			const BuildDatasets = async () => {
				datasets.value = [];

				if (metric.value == "forecast") {
					await GetForecast();
				} else {
					if (!props.report.results) return [];

					const sets = [];

					const set1 = {
						label: "Current Period",
						data: [],
						fill: false,
						borderColor: props.colors[0],
						tension: 0.1,
						spanGaps: true
					};
					if (isLastQuarter.value === true) {
						set1.segment = {
							borderColor: (ctx) => lineChartHelper.last(ctx, labels.value.length, "rgb(0,0,0,0.2)"),
							borderDash: (ctx) => lineChartHelper.last(ctx, labels.value.length, [6, 6])
						};
					}

					const groups = results.value.reduce((acc, row) => {
						const q = row[dateField.value]; // moment(row[dateField.value]).format("MMM YYYY");

						if (!labels.value.find((x) => x == q)) return acc;

						if (!acc[q]) {
							acc[q] = [];
						}

						acc[q].push(row);
						return acc;
					}, []);

					labels.value.forEach((label, i, self) => {
						set1.data.push(ProcessMetric(groups[label] || [], false));
					});
					set1.pointRadius = set1.data.map((d, i, self) => {
						if (i === self.length - 1) {
							return 3;
						} else {
							return 3;
						}
					});
					datasets.value.push(set1);

					if (showCompare.value && props.report.compare) {
						const set2 = {
							label: "Previous Period",
							data: [],
							fill: false,
							borderColor: props.colors[1],
							tension: 0.1,
							spanGaps: true
						};
						if (isLastQuarter.value === true) {
							set2.segment = {
								borderColor: (ctx) =>
									lineChartHelper.last(ctx, labels.value.length, "rgb(0,0,0,0.2)"),
								borderDash: (ctx) => lineChartHelper.last(ctx, labels.value.length, [6, 6])
							};
						}

						const groups = compare.value.reduce((acc, row) => {
							const q = row[dateField.value];

							if (!compareLabels.value.find((x) => x == q)) return acc;

							if (!acc[q]) {
								acc[q] = [];
							}

							acc[q].push(row);
							return acc;
						}, []);

						compareLabels.value.forEach((label, i, self) => {
							set2.data.push(ProcessMetric(groups[label] || [], true));
						});
						set2.pointRadius = set2.data.map((d, i, self) => {
							if (i === self.length - 1) {
								return 3;
							} else {
								return 3;
							}
						});
						datasets.value.push(set2);
					}
				}

				if (!props.breakdown?.breakdown?.breakdownFilter) {
					compare_datasets.value[metric.value] = {
						labels: labels.value,
						datasets: datasets.value
					};
				}
			};

			const ProcessMetric = (rows, isCompare) => {
				switch (metric.value) {
					case "totalBillings": {
						return !isCompare
							? $financial.getTotalBillings(rows)
							: $financial.compare.getTotalBillings(rows);
					}
					case "teamingValue": {
						return !isCompare
							? $financial.getTeamingValue(rows)
							: $financial.compare.getTeamingValue(rows);
					}
					case "teamingRevenue": {
						return !isCompare
							? $financial.getTeamingRevenue(rows)
							: $financial.compare.getTeamingRevenue(rows);
					}
					case "executiveSearch": {
						return !isCompare ? $financial.getExecutive(rows) : $financial.compare.getExecutive(rows);
					}
					case "consulting": {
						return !isCompare
							? $financial.getConsulting(rows)
							: $financial.compare.getConsulting(rows);
					}
					case "im": {
						return !isCompare ? $financial.getIM(rows) : $financial.compare.getIM(rows);
					}
					case "strategy": {
						return !isCompare
							? $financial.getStrategyBillings(rows)
							: $financial.compare.getStrategyBillings(rows);
					}
					case "filledJobValue": {
						return !isCompare
							? $financial.getFilledJobValue(rows)
							: $financial.compare.getFilledJobValue(rows);
					}
					case "strategyTaken": {
						return !isCompare
							? $financial.getStrategyTaken(rows)
							: $financial.compare.getStrategyTaken(rows);
					}
					case "taken": {
						return !isCompare
							? $financial.getAssignmentsTaken(rows)
							: $financial.compare.getAssignmentsTaken(rows);
					}
					case "filled": {
						return !isCompare
							? $financial.getAssignmentsFilled(rows)
							: $financial.compare.getAssignmentsFilled(rows);
					}
					case "headcount": {
						return !isCompare ? $financial.getHeadcount(rows) : $financial.compare.getHeadcount(rows);
					}
					default: {
						return rows.reduce((acc, v) => acc + v["totalBillings"], 0) ?? 0;
					}
				}
			};

			const GetForecast = async () => {
				try {
					const response = await http.get("/api/v2/financial/chart/forecast/" + year.value);
					datasets.value = response.data;
					return response.data;
				} catch (err) {
					console.error(err);
					return [];
				}
			};

			watch(
				() => props.report.filter,
				() => {
					BuildDatasets();
				},
				{ deep: true, immediate: true }
			);
			watch(
				() => metric.value + year.value,
				() => {
					BuildDatasets();
				}
			);
			watch(
				() => showCompare.value,
				(n) => {
					datasets.value[1].hidden = !n;
				}
			);
			watch(
				() => results.value,
				() => {
					BuildDatasets();
				}
			);

			return {
				hasReportData,
				showCompare,
				metric,
				year,
				years,
				labels,
				datasets,
				options
			};
		}
	};
</script>
